/*

this is a variant of the TodoListItem component that is used inside todo edit to display a sub todo.
includes inline editing of the sub todo.
*/

import { Box } from "arkh";
import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import {
  TodoListItemContainer,
  TodoListItemContainerContent,
} from "widgets/src/TodoListItem/TodoListItemContainer";
import { TodoListItemMainContentDetails } from "widgets/src/TodoListItem/TodoListItemMainContentDetails";
import { TodoListItemPriority } from "widgets/src/TodoListItem/TodoListItemPriority";
import { TodoListItemMainContentText } from "widgets/src/TodoListItem/TodoListItemMainContentText";

import { TodoEditNewMainFooterResponseNumber } from "./TodoEditNewMainFooter/TodoEditNewMainFooterResponseNumber";
import { TodoFooterCircleStatus } from "../../TodoCategory/TodoFooterCircleStatus";
import { TodoCircleStatusWithResponse } from "../../TodoCategory/TodoCircleStatusWithResponse";
import { useOpenCamera } from "@/src/components/mobile/useImageActionSheet";
import { useTodoEditResponseImage } from "./TodoEditNewMainFooter/useTodoEditResponseImage";
import { TodoEditNewMainFooterResponseText } from "./TodoEditNewMainFooter/TodoEditNewMainFooterResponseText";
import { TodoEditNewMainFooterResponseImage } from "./TodoEditNewMainFooter/TodoEditNewMainFooterResponseImage";
import { TodoListItemSubTodo_todo$key } from "./__generated__/TodoListItemSubTodo_todo.graphql";
import { TodoListItemSubTodoResponse_todo$key } from "./__generated__/TodoListItemSubTodoResponse_todo.graphql";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { TodoListItemFooter } from "widgets/src/TodoListItem/TodoListItemFooter/TodoListItemFooter";

export const TodoListItemSubTodo = React.memo(TodoListItemSubTodoC);
function TodoListItemSubTodoC(props: {
  disabled?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isNew?: boolean;
  todo: TodoListItemSubTodo_todo$key;

  openTodo(fromResponse: boolean, todoId: string): void;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoListItemSubTodo_todo on Todo {
        id
        status
        ordinal
        response {
          __typename
        }
        canWrite
        canChangeStatus

        ...TodoFooterCircleStatus_todo
        ...TodoCircleStatusWithResponse_todo
        ...TodoListItemSubTodoResponse_todo
        ...TodoListItemMainContentText_todo
        ...TodoListItemPriority_todo
        ...TodoListItemFooter_todo

        ...TodoListItemMainContentDetails_todo
      }
    `,
    props.todo
  );

  const { addImage } = useTodoEditResponseImage({ todoId: todo.id });

  const { openCamera } = useOpenCamera({
    onAddImage(uri, { width, height }) {
      addImage(uri, { width, height });
      props.openTodo(true, todo.id);
    },
  });

  const onStatusUpdateNeedsAction = useCallback(
    (focusResponse: boolean) => {
      if (focusResponse) {
        if (todo.response?.__typename === "TodoResponseImage") {
          openCamera();
        } else {
          props.openTodo(true, todo.id);
        }
      } else {
        props.openTodo(false, todo.id);
      }
    },
    [openCamera, props, todo.id, todo.response?.__typename]
  );

  const hasInlineResponse =
    todo.response?.__typename === "TodoResponseNumber" ||
    todo.response?.__typename === "TodoResponseText" ||
    todo.response?.__typename === "TodoResponseImage";
  const isDisabled = !todo.canChangeStatus;
  const { currentEmployeeId } = usePersistedEmployeeId();

  const sizeClass = useSizeClass().width;
  return (
    <TodoListItemContainer
      onPress={() => props.openTodo(false, todo.id)}
      disabled={props.disabled}
      isNew={props.isNew}
      isFirst={props.isFirst}
      isLast={props.isLast}
    >
      <Box spaceVertical="small" />
      <TodoListItemContainerContent>
        <TodoListItemPriority todo={todo} />

        {hasInlineResponse ? (
          <TodoFooterCircleStatus todo={todo} />
        ) : (
          <TodoCircleStatusWithResponse
            todo={todo}
            disabled={isDisabled}
            onStatusUpdateNeedsAction={onStatusUpdateNeedsAction}
          />
        )}
        <TodoListItemMainContentText todo={todo} />
        <TodoListItemMainContentDetails todo={todo} />
      </TodoListItemContainerContent>
      <TodoListItemSubTodoResponse todo={todo} />
      <TodoListItemFooter
        todo={todo}
        sizeClass={sizeClass}
        currentEmployeeId={currentEmployeeId ?? undefined}
      />
    </TodoListItemContainer>
  );
}

function TodoListItemSubTodoResponse(props: {
  todo: TodoListItemSubTodoResponse_todo$key;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoListItemSubTodoResponse_todo on Todo {
        status
        ...TodoEditNewMainFooterResponseImage_todo
        ...TodoEditNewMainFooterResponseNumber_todo
        ...TodoEditNewMainFooterResponseText_todo
        response {
          ... on TodoResponseNumber {
            __typename
          }
          ... on TodoResponseImage {
            __typename
          }
          ... on TodoResponseText {
            __typename
          }
        }
      }
    `,
    props.todo
  );
  const isDone = todo.status === "DONE";
  return (
    <>
      {todo.response?.__typename === "TodoResponseNumber" ? (
        <Wrapper isDone={isDone}>
          <TodoEditNewMainFooterResponseNumber todo={todo} />
        </Wrapper>
      ) : null}
      {todo.response?.__typename === "TodoResponseText" ? (
        <Wrapper isDone={isDone}>
          <TodoEditNewMainFooterResponseText todo={todo} />
        </Wrapper>
      ) : null}
      {todo.response?.__typename === "TodoResponseImage" ? (
        <Wrapper isDone={isDone}>
          <TodoEditNewMainFooterResponseImage todo={todo} />
        </Wrapper>
      ) : null}
    </>
  );
}
function Wrapper(props: { children: React.ReactNode; isDone: boolean }) {
  return (
    <Box
      spaceTop="medium"
      spaceBottom="small"
      style={{ opacity: props.isDone ? 0.7 : 1 }}
    >
      {props.children}
    </Box>
  );
}
